// @flow
import * as React from 'react';

import { Outlet, useNavigate } from 'react-router-dom';

import { selectOrder, type Order, type OrderId } from '@gandi/billing';
import { selectCartIsCommitted, selectCartUUID } from '@gandi/cart';

import { withRouter, type WithRouterProps } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { ErrorBoundary } from '../Layout/containers/ErrorBoundary/ErrorBoundary';
import { useTrackingProps } from '../hooks/tracking';

import { SVGDefs } from './SVGDefs';

type Props = {
  ...WithRouterProps<>,
  params: {
    orderId?: OrderId,
  },
};

function Root(props: Props): React.Node {
  const navigate = useNavigate();
  const cartIsCommitted: boolean = useAppSelector(selectCartIsCommitted);
  const cartId: ?string = useAppSelector(selectCartUUID);
  const order: ?Order = useAppSelector(state => {
    if (props.params.orderId) {
      return selectOrder(state, {
        orderId: props.params.orderId,
      });
    }
    return null;
  });

  const orderHasToBePaid = order && order.status === 'unpaid' && order.must_pay;
  const redirectToOverview = cartIsCommitted && !orderHasToBePaid;

  React.useEffect(() => {
    // redirect to the overview only if one cart has been found and committed
    // but not if an order has to be paid
    if (redirectToOverview && cartId) {
      navigate(`/cart/${cartId}/overview`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useTrackingProps();

  return (
    <ErrorBoundary>
      <Outlet />

      <SVGDefs>
        <linearGradient id="gradient">
          <stop stopColor="var(--color-duck-blue-200)" />
          <stop offset="1" stopColor="var(--color-acid-green-600)" />
        </linearGradient>
      </SVGDefs>
    </ErrorBoundary>
  );
}

export default (withRouter(Root): React.ComponentType<{}>);
